import React, { useState, useMemo } from 'react';
import { Card, Select, Checkbox } from 'antd';
import ClipLoader from 'react-spinners/ClipLoader';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { CheckboxValueType } from 'antd/es/checkbox/Group';

// Chart.js 등록
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const { Option } = Select;

interface PaymentTimeSlotTrendsDataPoint {
  dayOfWeek: number; // 1=일요일, 2=월요일, ..., 7=토요일
  dayOfWeekString: string; // "월요일"
  timeSlots: {
    timeSlot: number; // 0-47
    timeSlotString: string; // "09:00", "09:30"
    totalCount: number;
    app: number;
    naverStore: number;
    kakaoReservation: number;
    ezwel?: number;
    appAmount: number;
    naverStoreAmount: number;
    kakaoReservationAmount: number;
    ezwelAmount?: number;
  }[];
}

interface PaymentTimeSlotTrendsChartProps {
  data: PaymentTimeSlotTrendsDataPoint[];
  isLoading: boolean;
}

export const PaymentTimeSlotTrendsChart: React.FC<PaymentTimeSlotTrendsChartProps> = ({ data, isLoading }) => {
  const [viewMode, setViewMode] = useState<'count' | 'amount'>('count');
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['total']);

  const viewModeLabels = {
    count: '결제 건수',
    amount: '결제 금액',
  };

  const channelOptions = [
    { value: 'total', label: '전체', color: '#8B5CF6' },
    { value: 'app', label: '앱', color: '#3B82F6' },
    { value: 'naverStore', label: '네이버', color: '#22C55E' },
    { value: 'kakaoReservation', label: '카카오', color: '#EAB308' },
    { value: 'ezwel', label: '이지웰', color: '#0D6EFD' },
  ];

  // 요일별 고정 색상
  const dayOfWeekColors = [
    '#FF6B6B', // 일요일 - 빨강
    '#4ECDC4', // 월요일 - 청록
    '#45B7D1', // 화요일 - 파랑
    '#96CEB4', // 수요일 - 초록
    '#FFEAA7', // 목요일 - 노랑
    '#DDA0DD', // 금요일 - 자주
    '#FFA07A', // 토요일 - 주황
  ];

  // 차트 데이터 생성
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // 시간대 라벨 생성 (00:00 ~ 23:30)
    const timeLabels = Array.from({ length: 48 }, (_, i) => {
      const hour = Math.floor(i / 2);
      const minute = i % 2 === 0 ? '00' : '30';
      return `${hour.toString().padStart(2, '0')}:${minute}`;
    });

    // 각 요일별 데이터셋 생성 (총 7개 선)
    const datasets = data
      .map((dayData) => {
        const dayColorIndex = dayData.dayOfWeek - 1; // 1=일요일이므로 -1
        const baseColor = dayOfWeekColors[dayColorIndex] || '#8B5CF6';

        return selectedChannels.map((channel) => {
          let values: number[];
          let label: string;
          let color: string;

          if (channel === 'total') {
            values = dayData.timeSlots.map((slot) =>
              viewMode === 'count'
                ? slot?.totalCount || 0
                : (slot?.appAmount || 0) +
                  (slot?.naverStoreAmount || 0) +
                  (slot?.kakaoReservationAmount || 0) +
                  (slot?.ezwelAmount || 0),
            );
            label = `${dayData.dayOfWeekString} - 전체`;
            color = baseColor;
          } else {
            const channelKey = channel as 'app' | 'naverStore' | 'kakaoReservation' | 'ezwel';
            values = dayData.timeSlots.map((slot) =>
              viewMode === 'count'
                ? slot?.[channelKey] || 0
                : (slot?.[`${channelKey}Amount` as keyof typeof slot] as number) || 0,
            );
            const channelLabel = channelOptions.find((opt) => opt.value === channel)?.label || channel;
            label = `${dayData.dayOfWeekString} - ${channelLabel}`;
            color = channelOptions.find((opt) => opt.value === channel)?.color || baseColor;
          }

          return {
            label,
            data: values,
            borderColor: color,
            backgroundColor: color + '20',
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 4,
            tension: 0.1,
          };
        });
      })
      .flat();

    return {
      labels: timeLabels,
      datasets,
    };
  }, [data, viewMode, selectedChannels]);

  // Chart.js 옵션
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'line',
          padding: 15,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (context) {
            const value = context.parsed.y;
            const suffix = viewMode === 'count' ? '건' : '원';
            return `${context.dataset.label}: ${value.toLocaleString()}${suffix}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: '시간대',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        ticks: {
          maxTicksLimit: 12,
          font: {
            size: 10,
          },
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: viewMode === 'count' ? '결제 건수' : '결제 금액 (원)',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            if (viewMode === 'amount') {
              return (value as number).toLocaleString();
            }
            return value;
          },
          font: {
            size: 10,
          },
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  const handleChannelChange = (checkedValues: string[]) => {
    setSelectedChannels(checkedValues.length > 0 ? checkedValues : ['total']);
  };

  if (isLoading) {
    return (
      <Card className="chart" title="요일별 결제 시간대 트렌드" bordered={false}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: '49%' }}>
          <ClipLoader />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className="chart"
      title="요일별 결제 시간대 트렌드"
      bordered={false}
      style={{ minWidth: '49%' }}
      extra={
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Checkbox.Group
            value={selectedChannels}
            onChange={(checkedValue: CheckboxValueType[]) => handleChannelChange(checkedValue as string[])}
            style={{ display: 'flex', gap: '8px' }}>
            {channelOptions.map((option) => (
              <Checkbox key={option.value} value={option.value}>
                <span style={{ color: option.color, fontWeight: 'bold' }}>●</span> {option.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
          <Select value={viewMode} onChange={setViewMode} style={{ width: 120 }}>
            {Object.entries(viewModeLabels).map(([key, label]) => (
              <Option key={key} value={key}>
                {label}
              </Option>
            ))}
          </Select>
        </div>
      }>
      <div style={{ padding: '0', height: '400px' }}>
        {data && data.length > 0 ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#999',
            }}>
            해당 기간에 결제 데이터가 없습니다.
          </div>
        )}
      </div>
    </Card>
  );
};

export default PaymentTimeSlotTrendsChart;
